// SEO meta registry for build-time prerender. Each entry produces a
// `dist/<path>/index.html` with route-specific <title>, <meta description>,
// og:* and canonical baked into the static HTML — so Google's snippet picker
// sees a unique description per URL instead of the SPA fallback.
//
// Keep descriptions 140–160 chars, benefit-led, with a verb. Titles ≤60 chars.
// Add new public routes here when they ship. Private/admin/auth routes are
// intentionally omitted — they're already noindex via RouteMetaGuard.

export interface RouteMeta {
  path: string;
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  // Optional full JSON-LD payload to bake into this route's static HTML at build
  // time. Most routes rely on Helmet client-side; set this for routes where
  // structured data must reach non-JS crawlers (GPTBot, ClaudeBot, PerplexityBot,
  // Google-Extended) — see src/lib/homepageSchema.ts for the homepage example.
  jsonLd?: Record<string, unknown>;
}

import { HOMEPAGE_TITLE, HOMEPAGE_DESCRIPTION, HOMEPAGE_JSON_LD } from "../src/lib/homepageSchema";
import { ABOUT_TITLE, ABOUT_DESCRIPTION, ABOUT_JSON_LD } from "../src/data/aboutData";
import { STATE_GRANTS } from "../src/data/statesGrants";
import { STATE_CLOSING_COSTS, estimateClosingCostDollar } from "../src/data/statesClosingCosts";
import { STATE_MORTGAGE_RATES } from "../src/data/statesMortgageRates";
import { METRO_RATES } from "../src/data/metrosMortgageRates";
import { METRO_GRANTS } from "../src/data/metrosGrants";
import { METRO_CLOSING_COSTS, estimateMetroClosingCostDollar } from "../src/data/metrosClosingCosts";
import { STATE_FIRST_TIME_BUYER } from "../src/data/statesFirstTimeBuyer";
import { METRO_FIRST_TIME_BUYER } from "../src/data/metrosFirstTimeBuyer";

// Programmatic /grants/:state entries — one per state. Generated so the 51
// pages get baked static <title>/<meta description> in dist/grants/<slug>/index.html.
const grantsByStateRoutes: RouteMeta[] = STATE_GRANTS.map((s) => ({
  path: `/grants/${s.slug}`,
  title: `${s.name} First-Time Home Buyer Grants & DPA (${s.abbr})`,
  description: `Stack federal, ${s.hfaShort} state, county, and employer down payment assistance available in ${s.name}. Free finder — see your total in 30 seconds.`,
}));

// Programmatic /closing-costs/:state entries — 51 pages baked with unique meta.
const closingCostsByStateRoutes: RouteMeta[] = STATE_CLOSING_COSTS.map((s) => ({
  path: `/closing-costs/${s.slug}`,
  title: `${s.name} Closing Costs 2026 — Buyer Breakdown (${s.abbr})`,
  description: `${s.name} buyer closing costs average ~${s.avgCostPct}% of price ($${estimateClosingCostDollar(s).toLocaleString("en-US")} on a $${s.avgHomePrice.toLocaleString("en-US")} home). See breakdown, transfer tax, and how to lower them.`,
}));

// Programmatic /mortgage-rates/:state entries — 51 pages with per-state meta.
const mortgageRatesByStateRoutes: RouteMeta[] = STATE_MORTGAGE_RATES.map((s) => ({
  path: `/mortgage-rates/${s.slug}`,
  title: `${s.name} Mortgage Rates 2026 — Today's Averages (${s.abbr})`,
  description: `${s.name} 30-yr mortgage rates today, ${s.abbr === "US" ? "" : ""}with first-time buyer programs, payment math on a $${s.medianLoanAmount.toLocaleString("en-US")} loan, and how ${s.abbr} compares to the national average.`,
}));

// Programmatic /mortgage-rates/city/:city entries — 100 top metros.
const mortgageRatesByCityRoutes: RouteMeta[] = METRO_RATES.map((m) => ({
  path: `/mortgage-rates/city/${m.slug}`,
  title: `${m.city}, ${m.stateAbbr} Mortgage Rates 2026 — Today's Averages`,
  description: `${m.city} mortgage rates today with payment math on a $${m.medianLoanAmount.toLocaleString("en-US")} loan, how ${m.city} compares to national, and top ${m.stateAbbr} lenders.`,
}));

// Programmatic /grants/city/:city entries — 100 top metros.
const grantsByCityRoutes: RouteMeta[] = METRO_GRANTS.map((m) => ({
  path: `/grants/city/${m.slug}`,
  title: `${m.city}, ${m.stateAbbr} Down Payment Assistance & First-Time Buyer Grants`,
  description: `Every layer of homebuyer assistance in ${m.city}, ${m.stateAbbr} — city and county DPA, ${m.state?.hfaShort ?? "state"} programs, federal aid, and employer help — stacked into one total.`,
}));

// Programmatic /closing-costs/city/:city entries — 100 top metros.
const closingCostsByCityRoutes: RouteMeta[] = METRO_CLOSING_COSTS.map((m) => ({
  path: `/closing-costs/city/${m.slug}`,
  title: `${m.city}, ${m.stateAbbr} Closing Costs 2026 — Buyer Breakdown`,
  description: `${m.city} closing costs average ~${m.avgCostPct}% of price ($${estimateMetroClosingCostDollar(m).toLocaleString("en-US")} on a $${m.medianHomePrice.toLocaleString("en-US")} home). See breakdown and transfer tax detail.`,
}));

// Programmatic /first-time-buyer/:state — 51 entries.
const firstTimeBuyerByStateRoutes: RouteMeta[] = STATE_FIRST_TIME_BUYER.map((s) => ({
  path: `/first-time-buyer/${s.slug}`,
  title: `First-Time Home Buyer Programs in ${s.name} (${s.abbr}) — 2026 Guide`,
  description: `${s.name} first-time buyer programs: ${s.flagshipProgram} from ${s.hfaShort}, eligibility, credit score requirements, and how to stack with FHA, VA, USDA loans.`,
}));

// Programmatic /first-time-buyer/city/:city — top 50 metros.
const firstTimeBuyerByCityRoutes: RouteMeta[] = METRO_FIRST_TIME_BUYER.map((m) => ({
  path: `/first-time-buyer/city/${m.slug}`,
  title: `First-Time Home Buyer Programs in ${m.city}, ${m.stateAbbr} — 2026`,
  description: `${m.city}, ${m.stateAbbr} first-time buyer programs: ${m.localFthbProgram ?? m.state?.flagshipProgram ?? "state flagship"}, eligibility, and how to qualify on a $${m.medianFirstTimePrice.toLocaleString("en-US")} starter home.`,
}));

// Static guides (src/data/guides.ts): title/description only for now.
// guides.ts imports image assets via the "@/" alias, which Vite's app
// bundler resolves fine but esbuild's config-loading step (how Node
// evaluates vite.config.ts's own import graph, including this file) cannot
// — it has no loader for .jpg/.webp.asset.json outside the full app bundle.
// So guides.ts (or anything importing it) can't be pulled in here directly.
// jsonLd for these 3 guides is NOT yet baked at build time — tracked in
// ~/Projects/HomeApproach.com/gecko/aeo-geo-takeaways.md as a real gap
// needing a small standalone extraction step (run guides.ts through a
// separate Node/tsx process outside vite.config.ts's own module graph,
// emit resolved title/description/jsonLd as plain JSON, import that JSON
// here) rather than importing guides.ts directly. Client-side jsonLd via
// buildGuideJsonLd() in src/lib/guideSchema.ts still fires in GuidePage.tsx
// same as before — this only affects non-JS-crawler visibility, not users
// or JS-rendering crawlers.
const staticGuideRoutes: RouteMeta[] = [
  {
    path: "/guides/first-time-homebuyer-complete-guide",
    title: "The Definitive First-Time Homebuyer's Guide | Home Approach",
    description:
      "How to close on your first property and accelerate your journey to financial freedom.",
  },
  {
    path: "/guides/homeowner-first-year-playbook",
    title: "The Homeowner's First-Year Playbook | Home Approach",
    description:
      "A month-by-month plan for your first 12 months of ownership — paperwork, maintenance, taxes, and equity.",
  },
  {
    path: "/guides/credit-and-down-payment-runway",
    title: "The Credit & Down Payment Runway Guide | Home Approach",
    description:
      "The 12- to 24-month plan to raise your score, stack your savings, and step into your first mortgage with leverage.",
  },
];

export const prerenderRoutes: RouteMeta[] = [
  {
    path: "/",
    title: HOMEPAGE_TITLE,
    description: HOMEPAGE_DESCRIPTION,
    jsonLd: HOMEPAGE_JSON_LD,
  },
  {
    path: "/about",
    title: ABOUT_TITLE,
    description: ABOUT_DESCRIPTION,
    jsonLd: ABOUT_JSON_LD,
  },
  {
    path: "/experts",
    title: "Find Your Home Expert — Agents, Lenders, Inspectors",
    description:
      "Browse vetted real estate agents, mortgage lenders, home inspectors, attorneys, and insurance pros. Get matched with trusted local experts for free.",
  },
  // Featured experts (small static set; blog/expert detail pages with many
  // slugs continue to rely on Helmet since Googlebot does render JS).
  {
    path: "/experts/ruben-nazario",
    title: "Ruben Nazario — Real Estate Agent | Home Approach",
    description:
      "Connect with NJ real estate agent Ruben Nazario. Buy, sell, or invest with a Home Approach founding ambassador — bilingual (English/Spanish), 10+ years experience.",
  },
  {
    path: "/learn/ruben",
    title: "Real Talk Realty with Ruben Nazario | Home Approach",
    description:
      "Free homebuying, selling, and investment-property mini-courses from realtor Ruben Nazario, Home Approach's founding brand ambassador. Earn rewards as you learn.",
  },
  {
    path: "/experts/randy-nemeroff",
    title: "Randy Nemeroff — Real Estate Attorney | Home Approach",
    description:
      "Work with real estate attorney Randy Nemeroff on contracts, closings, and title issues. Trusted legal guidance for first-time and seasoned homebuyers.",
  },
  {
    path: "/experts/gordon-oliver",
    title: "Gordon Oliver — Real Estate Agent | Home Approach",
    description:
      "Find your next home with real estate agent Gordon Oliver. Local market insight, smart offers, and a guided buying experience — featured on Home Approach.",
  },
  {
    path: "/experts/valon-nikci",
    title: "Valon Nikci — Real Estate Agent | Home Approach",
    description:
      "Buy or sell with real estate agent Valon Nikci. Get neighborhood expertise, sharp negotiation, and a clear path to closing on your next home.",
  },
  {
    path: "/experts/joshua-stone",
    title: "Joshua Stone, CPI — Home Inspector NY & CT | Home Approach",
    description:
      "Book Joshua Stone, Certified Master Home Inspector serving NY & CT. 7+ years and thousands of inspections — fast scheduling for time-sensitive deals.",
  },
  {
    path: "/blog",
    title: "Home Approach Blog — Homebuying, Mortgages & Homeownership",
    description:
      "Practical guides on mortgages, credit, down payments, and homeownership. Written by experts to help you buy smarter and own with confidence.",
  },
  {
    path: "/guides",
    title: "Homebuyer Guides — Step-by-Step Playbooks | Home Approach",
    description:
      "Free, in-depth guides covering pre-approval, house hunting, closing, and the first 90 days as a homeowner. Built for first-time buyers.",
  },
  ...staticGuideRoutes,
  {
    path: "/webinars",
    title: "Free Homebuyer Webinars — Mortgages, Credit & DPA",
    description:
      "Live and on-demand webinars from housing pros on mortgages, down payment assistance, credit, and homebuying strategy. Register free.",
  },
  {
    path: "/tools",
    title: "All Free Homebuyer Tools & Guides | Home Approach",
    description:
      "Every Home Approach tool and guide in one searchable directory: mortgage calculators, affordability, down payment assistance, credit readiness, closing costs, and more. All free.",
  },
  {
    path: "/tools/right-time-to-buy",
    title: "Is Now the Right Time to Buy? Live Rate + Wait vs. Buy",
    description:
      "See today's 30-yr mortgage rate and model what waiting 6, 12, or 24 months costs in payment, price, and equity. Updated daily.",
  },
  {
    path: "/mortgage-calculators",
    title: "Mortgage Calculators — Payment, PMI & Amortization",
    description:
      "Calculate your monthly mortgage payment, principal & interest, PMI, taxes, and full amortization. Free, no signup, instant results.",
  },
  {
    path: "/rent-vs-buy",
    title: "Rent vs. Buy Calculator — Wealth Over Time | Home Approach",
    description:
      "Compare renting vs. buying over 5, 10, and 30 years. See break-even point, equity growth, and projected wealth side-by-side.",
  },
  {
    path: "/down-payment-planner",
    title: "Down Payment Planner — Hit Your Savings Goal Faster",
    description:
      "Set your target down payment, track monthly progress, and earn Approach Points for every milestone you hit. Stay on pace to closing day.",
  },
  {
    path: "/refi-optimizer",
    title: "Refinance Optimizer — Break-Even & Interest Savings",
    description:
      "Find your refinance break-even, monthly savings, and total interest reduction. Decide whether refinancing makes sense at today's rates.",
  },
  {
    path: "/home-value",
    title: "Free Home Value Estimator — Instant Home Worth",
    description:
      "Get an instant estimate of your home's market value. See recent comps and equity built — free, no signup required.",
  },
  // /closing-costs is behind AuthGate — omitted intentionally
  {
    path: "/neighborhood-explorer",
    title: "Neighborhood Explorer — Schools, Demographics & AI Insights",
    description:
      "Research any ZIP code: schools, demographics, affordability, and AI-generated neighborhood insights to find where you'll thrive.",
  },
  {
    path: "/hero-rewards",
    title: "How Rewards Work | Home Approach",
    description:
      "The complete guide to Approach Points: how you earn, how milestones unlock cashout, the four redemption tiers, VIP access, and how rewards are funded.",
  },
  {
    path: "/grant-finder",
    title: "Down Payment Grant Finder — Free Money for Homebuyers",
    description:
      "Find down payment assistance grants and first-time buyer programs in your ZIP code. Stack thousands in free money toward your purchase.",
  },
  {
    path: "/credit-readiness",
    title: "Credit Readiness Check — Mortgage-Ready Score Audit",
    description:
      "See if your credit is mortgage-ready. Get a personalized score audit, lender match, and a 30-day plan to boost your score.",
  },
  {
    path: "/affordability-simulator",
    title: "Affordability Simulator — See Homes in Your Budget",
    description:
      "Enter your income and see real homes you can afford, with AI-generated visuals. Adjust down payment and rate to model every scenario.",
  },
  {
    path: "/homeowner-checkup",
    title: "Homeowner Checkup — Equity, Refi & Insurance Snapshot",
    description:
      "Get a free 60-second checkup on your home's equity, refinance opportunity, insurance fit, and tax efficiency. Built for current homeowners.",
  },
  {
    path: "/mortgage-glossary",
    title: "Mortgage Glossary — Plain-English Definitions",
    description:
      "Plain-English definitions for every mortgage and homebuying term: APR, PMI, escrow, DTI, points, and 100+ more.",
  },
  {
    path: "/learn",
    title: "Home Approach Learn — Homebuyer Courses & Lessons",
    description:
      "Free homebuyer courses, sequential lessons, and printable checklists. Earn a completion certificate and Approach Points as you progress.",
  },
  // /rewards, /points-store, /leaderboard, /success-stories are behind AuthGate — omitted intentionally
  {
    path: "/homie",
    title: "Homie — Get Your Free Home Readiness Score",
    description:
      "Meet Homie, your honest friend for buying a home. Six quick questions, a free Readiness Score out of 100, and the one thing holding you back. No commission, no credit pull.",
  },
  {
    path: "/homie/chat",
    title: "Chat with Homie — Your Free AI Homebuying Guide",
    description:
      "Chat with Homie, your free AI guide for everything homebuying. Pre-approval, programs, calculators, and next steps — answered instantly.",
  },

  {
    path: "/lifemart",
    title: "LifeMart Members — Free Credit Score + Homebuyer Perks",
    description:
      "LifeMart members get a free credit score check, exclusive homebuyer perks, and bonus Approach Points. Verify and unlock instantly.",
  },
  {
    path: "/lp/agent",
    title: "Partner With Home Approach — Agents & Homebuying Experts",
    description:
      "Apply to bring clients Hero benefits, grant discovery, buyer-readiness tools, and a co-branded Home Approach partner experience.",
  },
  {
    path: "/lp/organizations",
    title: "For Employers — Homebuying as a Benefit | Home Approach",
    description:
      "Offer your team a homebuying benefit that actually works: tools, education, and rewards that move employees from renting to owning.",
  },
  {
    path: "/install",
    title: "Install the Home Approach App on Your Phone",
    description:
      "Install Home Approach as an app on iPhone or Android in 10 seconds. Faster access to tools, alerts, and your rewards dashboard.",
  },
  {
    path: "/privacy-policy",
    title: "Privacy Policy | Home Approach",
    description:
      "How Home Approach collects, uses, and protects your information. Read our full privacy policy and data handling practices.",
  },
  {
    path: "/terms-and-conditions",
    title: "Terms & Conditions | Home Approach",
    description:
      "The terms governing your use of Home Approach tools, content, rewards, and services. Last updated 2026.",
  },
  {
    path: "/check-my-score",
    title: "Check My Credit Score Free — No Hard Pull | Home Approach",
    description:
      "See your real credit score instantly — soft pull only, zero credit impact, no card required. Get a $5 reward after your first check.",
  },
  {
    path: "/free-credit-score",
    title: "Free Credit Score Check + $3 Reward — Soft Pull | Home Approach",
    description:
      "Check your credit score free via Credit Karma — soft inquiry, zero credit impact, no credit card. Home Approach adds a $3 welcome reward redeemable as a gift card after your first homebuying milestone.",
  },
  // NOTE: Lovable SPA hosting currently serves root index.html for all routes —
  // per-route prerender files are written at build time but not delivered.
  // Googlebot renders JS so correct meta is seen eventually (P2 issue).
  // Fix: add vercel.json filesystem-first routing if/when Lovable supports it.
  ...grantsByStateRoutes,
  ...closingCostsByStateRoutes,
  ...mortgageRatesByStateRoutes,
  ...mortgageRatesByCityRoutes,
  ...grantsByCityRoutes,
  ...closingCostsByCityRoutes,
  ...firstTimeBuyerByStateRoutes,
  ...firstTimeBuyerByCityRoutes,
];